import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { Close } from '@mui/icons-material';
import { AlertTriangle, ExternalLink, Network, RefreshCw } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import kgDatasetService from '../../services/kgDatasetService';
import {
  agentsCanSee,
  describeError,
  getDatasetStatusMeta,
  recordCount,
  recordLabel,
  unwrapList,
} from '../KnowledgeGraph/kgUtils';
import styles from './KnowledgeGraphDialog.module.scss';

/**
 * Attach knowledge-graph datasets to an agent.
 *
 * The API has no dataset picker: a dataset owns exactly one D3 folder, and
 * attaching that folder is what attaches the dataset. Sending someone off to
 * hunt for a folder whose name they have to recognise is a poor trade, so this
 * dialog lists DATASETS — with the one piece of information that actually
 * decides whether the agent will work — and writes the folder id through on save.
 *
 * That one piece of information: a dataset that is not `active` is invisible to
 * agents no matter how complete it looks. It is the single most common confusion
 * in the feature, so it is called out per row rather than buried in help text.
 */
/**
 * Dataset screens open in a new tab, never in place. Navigating away from the
 * agent builder would discard an unsaved new agent entirely — and even in edit
 * mode it throws away whatever hasn't auto-saved yet. Nobody clicking "why isn't
 * this one live?" is asking to lose their work.
 */
const openKgTab = (path) => {
  window.open(path, '_blank', 'noopener,noreferrer');
};

const KnowledgeGraphDialog = ({ open, onClose, onSave, initialData }) => {
  const { isDarkMode } = useTheme();

  const [datasets, setDatasets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);

  const muiTheme = useMemo(
    () =>
      createTheme({
        palette: {
          mode: isDarkMode ? 'dark' : 'light',
          primary: { main: '#e36250' },
          secondary: { main: '#8b5cf6' },
        },
      }),
    [isDarkMode]
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await kgDatasetService.listDatasets();
      setDatasets(unwrapList(data, 'datasets', 'kg_datasets'));
      setError(null);
    } catch (e) {
      setError(describeError(e, "Couldn't load your knowledge graph datasets."));
      setDatasets([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    load();
    // Ids can arrive as numbers or strings depending on where they were
    // persisted, so every comparison here goes through String().
    setSelectedIds((initialData?.datasets || []).map((d) => String(d.dataset_id)));
  }, [open, load, initialData]);

  const toggle = (dataset) => {
    const id = String(dataset.id);
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleSave = () => {
    const chosen = datasets
      .filter((d) => selectedIds.includes(String(d.id)))
      .map((d) => ({
        dataset_id: d.id,
        folder_id: d.folder_id,
        key: d.key,
        label: d.label,
      }));
    onSave({ datasets: chosen });
    onClose({ datasets: chosen });
  };

  const selectedDatasets = datasets.filter((d) => selectedIds.includes(String(d.id)));
  const inactiveSelected = selectedDatasets.filter((d) => !agentsCanSee(d));

  return (
    <ThemeProvider theme={muiTheme}>
      <Dialog
        open={open}
        onClose={() => onClose(initialData)}
        maxWidth={false}
        fullWidth
        scroll="paper"
        className={styles.dialog}
        slotProps={{
          backdrop: {
            sx: { backgroundColor: 'rgba(24, 24, 27, 0.4)', backdropFilter: 'blur(2px)' },
          },
        }}
        PaperProps={{ className: styles.dialogPaper }}
      >
        <DialogTitle className={styles.dialogTitle} component="div">
          <Box className={styles.titleRow}>
            <Typography component="h3" variant="h6" className={styles.title}>
              Knowledge Graph
            </Typography>
          </Box>
          <IconButton
            onClick={() => onClose(initialData)}
            size="small"
            className={styles.closeButton}
            aria-label="Close"
          >
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent className={styles.dialogContent}>
          <Typography variant="body2" className={styles.lead}>
            Pick the datasets this agent can query. It gets tools that filter, count and group
            over their facets exactly — the questions document search answers with a confident
            guess.
          </Typography>

          {error && <Alert severity="error">{error}</Alert>}

          {loading ? (
            <div className={styles.loading}>
              <CircularProgress size={22} />
            </div>
          ) : datasets.length === 0 ? (
            <div className={styles.empty}>
              <Network size={34} color="var(--text-secondary)" />
              <strong>No datasets yet</strong>
              <p>
                A knowledge graph dataset holds structured records — articles, orders, circulars
                — with the fields worth filtering on declared as facets.
              </p>
              <Button
                variant="outlined"
                size="small"
                startIcon={<ExternalLink size={14} />}
                onClick={() => openKgTab('/knowledge-graph')}
              >
                Create one
              </Button>
            </div>
          ) : (
            <div className={styles.list}>
              {datasets.map((dataset) => {
                const statusMeta = getDatasetStatusMeta(dataset);
                const records = recordCount(dataset);
                const visible = agentsCanSee(dataset);
                const checked = selectedIds.includes(String(dataset.id));
                return (
                  <label
                    key={dataset.id}
                    className={`${styles.row} ${checked ? styles.rowChecked : ''}`}
                  >
                    <Checkbox
                      size="small"
                      checked={checked}
                      onChange={() => toggle(dataset)}
                    />
                    <div className={styles.rowBody}>
                      <div className={styles.rowHead}>
                        <span className={styles.rowName}>{dataset.label}</span>
                        <Tooltip title={statusMeta.meaning}>
                          <span className={`${styles.pill} ${styles[statusMeta.tone]}`}>
                            {statusMeta.label}
                          </span>
                        </Tooltip>
                      </div>
                      <div className={styles.rowMeta}>
                        <code>{dataset.key}</code>
                        <span>{recordLabel(dataset, records)}</span>
                      </div>

                      {/* The thing that decides whether this actually works. */}
                      {!visible && (
                        <span className={styles.rowWarn}>
                          <AlertTriangle size={12} />
                          {records === 0
                            ? 'Empty and not activated — this agent will get nothing from it.'
                            : `${statusMeta.label} — agents can't query it until it is activated.`}
                          <button
                            type="button"
                            className={styles.rowLink}
                            onClick={(e) => {
                              e.preventDefault();
                              openKgTab(`/knowledge-graph/${dataset.id}?tab=settings`);
                            }}
                          >
                            Open it
                          </button>
                        </span>
                      )}
                    </div>
                  </label>
                );
              })}
            </div>
          )}

          {inactiveSelected.length > 0 && (
            <Alert severity="warning" className={styles.summaryAlert}>
              <strong>
                {inactiveSelected.length} of the datasets you picked{' '}
                {inactiveSelected.length === 1 ? 'is' : 'are'} not live.
              </strong>{' '}
              The attachment saves fine, but this agent gets no knowledge-graph tools from{' '}
              {inactiveSelected.map((d) => d.label).join(', ')} until{' '}
              {inactiveSelected.length === 1 ? 'it is' : 'they are'} activated.
            </Alert>
          )}

          {datasets.length > 0 && (
            <button type="button" className={styles.refresh} onClick={load}>
              <RefreshCw size={12} /> Refresh
            </button>
          )}
        </DialogContent>

        <DialogActions className={styles.dialogActions}>
          <Button
            onClick={() => onClose(initialData)}
            variant="text"
            className={styles.cancelButton}
          >
            Cancel
          </Button>
          <Button onClick={handleSave} variant="contained" className={styles.saveButton}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  );
};

export default KnowledgeGraphDialog;
